import { useState } from 'react';
import axios from 'axios';
import { getCookie } from './authService';

// Base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Helper function for API calls
const apiCall = async (endpoint, method = 'GET', data = null, config = {}) => {
  try {
    // Get token from cookie
    const token = getCookie('token');
    
    const defaultConfig = {
      method,
      url: `${API_BASE_URL}${endpoint}`,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Include cookies for authentication
    };
    
    // Add token to Authorization header if available
    if (token) {
      defaultConfig.headers['Authorization'] = `Bearer ${token}`;
    }

    // Merge configs
    const mergedConfig = { ...defaultConfig, ...config };

    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      if (data instanceof FormData) {
        // Remove Content-Type header for FormData to let browser set it with boundary
        delete mergedConfig.headers['Content-Type'];
        mergedConfig.data = data;
      } else {
        mergedConfig.data = data;
      }
    }

    const response = await axios(mergedConfig);
    return response.data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
};

export const useAdvertisementServices = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create a new advertisement with optional PDF and image upload
  const createAdvertisement = async (advertisementData, pdfFile = null, imageFile = null) => {
    setLoading(true);
    setError(null);
    try {
      // If there are files, use FormData to send multipart/form-data
      let data;
      let config = {};
      
      if (pdfFile || imageFile) {
        data = new FormData();
        // Add advertisement data to FormData
        Object.keys(advertisementData).forEach(key => {
          data.append(key, advertisementData[key]);
        });
        // Add PDF file if provided
        if (pdfFile) {
          data.append('pdfFile', pdfFile);
        }
        // Add image file if provided
        if (imageFile) {
          data.append('imageFile', imageFile);
        }
      } else {
        data = advertisementData;
      }

      const response = await apiCall('/api/v1/advertisements', 'POST', data, config);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating advertisement');
      setLoading(false);
      throw err;
    }
  };

  // Get all advertisements with filtering and pagination
  const getAdvertisements = async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams();
      
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = `/api/v1/advertisements${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiCall(url);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching advertisements');
      setLoading(false);
      throw err;
    }
  };

  // Get public advertisements (active only)
  const getPublicAdvertisements = async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams();
      
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = `/api/v1/advertisements/public${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiCall(url);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching public advertisements');
      setLoading(false);
      throw err;
    }
  };

  // Get featured advertisements
  const getFeaturedAdvertisements = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall('/api/v1/advertisements/featured');
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching featured advertisements');
      setLoading(false);
      throw err;
    }
  };

  // Get advertisement by ID
  const getAdvertisementById = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall(`/api/v1/advertisements/${id}`);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching advertisement');
      setLoading(false);
      throw err;
    }
  };

  // Update advertisement with optional PDF and image upload
  const updateAdvertisement = async (id, advertisementData, pdfFile = null, imageFile = null) => {
    setLoading(true);
    setError(null);
    try {
      // If there are files, use FormData to send multipart/form-data
      let data;
      let config = {};
      
      if (pdfFile || imageFile) {
        data = new FormData();
        // Add advertisement data to FormData
        Object.keys(advertisementData).forEach(key => {
          data.append(key, advertisementData[key]);
        });
        // Add PDF file if provided
        if (pdfFile) {
          data.append('pdfFile', pdfFile);
        }
        // Add image file if provided
        if (imageFile) {
          data.append('imageFile', imageFile);
        }
      } else {
        data = advertisementData;
      }

      const response = await apiCall(`/api/v1/advertisements/${id}`, 'PUT', data, config);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating advertisement');
      setLoading(false);
      throw err;
    }
  };

  // Delete advertisement
  const deleteAdvertisement = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall(`/api/v1/advertisements/${id}`, 'DELETE');
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting advertisement');
      setLoading(false);
      throw err;
    }
  };

  // Toggle advertisement status
  const toggleAdvertisementStatus = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall(`/api/v1/advertisements/${id}/toggle-status`, 'PATCH');
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Error toggling advertisement status');
      setLoading(false);
      throw err;
    }
  };

  // Toggle featured status
  const toggleFeaturedStatus = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall(`/api/v1/advertisements/${id}/toggle-featured`, 'PATCH');
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Error toggling featured status');
      setLoading(false);
      throw err;
    }
  };

  return {
    loading,
    error,
    createAdvertisement,
    getAdvertisements,
    getPublicAdvertisements,
    getFeaturedAdvertisements,
    getAdvertisementById,
    updateAdvertisement,
    deleteAdvertisement,
    toggleAdvertisementStatus,
    toggleFeaturedStatus
  };
};

export default useAdvertisementServices;

